import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Service } from '@/types/dashboard';
import { useToast } from "@/components/ui/use-toast";
import { fetchVisibleCategories } from '@/utils/categoryUtils';

export interface ServicePageProfile {
  id: string;
  company_name: string | null;
  profile_image_url: string | null;
  whatsapp_number: string | null;
  banner_image_url: string | null;
  currency: string | null;
  service_page_link: string | null;
  preferred_messaging: string | null;
  telegram_chat_id: number | null;
  first_name: string | null;
  last_name: string | null;
  profession: string | null;
  profile_bio: string | null;
  country: string | null;
}

export interface ServiceImage {
  id: string;
  service_id: string;
  image_url: string;
  created_at: string;
}

export interface ServiceWithImages extends Service {
  images: ServiceImage[];
  selectedImageIndex: number;
}

export interface ServiceCategory {
  id: string;
  name: string;
  user_id: string;
}

export interface UseServicePageResult {
  profile: ServicePageProfile | null;
  services: ServiceWithImages[];
  categories: ServiceCategory[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  servicePageNotFound: boolean;
  searchTerm: string;
  categoryId: string | null;
  setSearchTerm: (term: string) => void;
  setCategoryId: (id: string | null) => void;
  handleImageNavigation: (serviceId: string, direction: 'prev' | 'next') => void;
  loadMore: () => Promise<void>;
  resetPagination: () => void;
}

export function useServicePage(username: string): UseServicePageResult {
  const [profile, setProfile] = useState<ServicePageProfile | null>(null);
  const [services, setServices] = useState<ServiceWithImages[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [servicePageNotFound, setServicePageNotFound] = useState(false);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast } = useToast();

  // Add debug log when loading or services change
  useEffect(() => {
    console.log('useServicePage state change:', {
      username,
      isLoading,
      serviceCount: services.length,
      hasProfile: !!profile,
      categoriesCount: categories.length
    });
  }, [isLoading, services, profile, categories, username]);

  const resetPagination = useCallback(() => {
    setPage(0);
    setServices([]);
    setHasMore(false);
  }, []);

  useEffect(() => {
    resetPagination();
  }, [categoryId, searchTerm, resetPagination]);

  const fetchProfile = useCallback(async () => {
    try {
      let profileQuery = await supabase
        .from('profiles')
        .select('id, company_name, profile_image_url, whatsapp_number, banner_image_url, currency, service_page_link, preferred_messaging, telegram_chat_id, first_name, last_name, profession, profile_bio, country')
        .eq('service_page_link', username)
        .maybeSingle();
          
      if (!profileQuery.data) {
        profileQuery = await supabase
          .from('profiles')
          .select('id, company_name, profile_image_url, whatsapp_number, banner_image_url, currency, service_page_link, preferred_messaging, telegram_chat_id, first_name, last_name, profession, profile_bio, country')
          .ilike('service_page_link', `%${username}%`)
          .maybeSingle();
      }
      
      if (profileQuery.error || !profileQuery.data) {
        console.error('Error finding profile:', profileQuery.error);
        setServicePageNotFound(true);
        return null;
      }
      
      return profileQuery.data;
    } catch (error) {
      console.error('Error in fetchProfile:', error);
      return null;
    }
  }, [username]);

  const recordPageView = useCallback(async (profileId: string) => {
    if (!profileId) return;
    
    setTimeout(async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user?.id !== profileId) {
          await supabase.functions.invoke('increment-page-views', {
            body: { userId: profileId }
          });
          console.log("Page view recorded for:", profileId);
        }
      } catch (error) {
        console.error('Error incrementing page views:', error);
      }
    }, 1000);
  }, []);

  const fetchServices = useCallback(async (profileId: string, categoryFilter: string | null, searchFilter: string) => {
    if (!profileId) return { data: [] };
    
    try {
      const visibleCategories = await fetchVisibleCategories(profileId);
      const visibleCategoryIds = visibleCategories.map(cat => cat.id);
      
      let query = supabase
        .from('services')
        .select(`
          *,
          service_categories (
            id,
            name
          ),
          service_images (
            id,
            image_url
          )
        `)
        .eq('user_id', profileId)
        .eq('is_active', true);
      
      if (categoryFilter && visibleCategoryIds.includes(categoryFilter)) {
        query = query.eq('category_id', categoryFilter);
      }
      
      if (searchFilter) {
        query = query.ilike('name', `%${searchFilter}%`);
      }
      
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching services:', error);
        return { data: [] };
      }
      
      const processedData = processServicesWithImages(data || []);
      
      return { data: processedData };
    } catch (error) {
      console.error('Error in fetchServices:', error);
      return { data: [] };
    }
  }, []);

  const processServicesWithImages = (rawData: any[]): ServiceWithImages[] => {
    const serviceMap = new Map();
    
    console.log('Processing raw data:', rawData);
    
    rawData.forEach(item => {
      const serviceId = item.id;
      const serviceImages = item.service_images || [];
      const coverImageUrl = item.image_url;
      
      // Filter out any null/undefined images or images without URLs
      const validImages = Array.isArray(serviceImages) 
        ? serviceImages.filter(img => img && img !== null && img.image_url && img.image_url.trim() !== '') 
        : [];
      
      console.log(`Processing service ${serviceId}:`, {
        serviceName: item.name,
        originalImageCount: serviceImages.length,
        validImageCount: validImages.length,
        coverImageUrl,
        validImages
      });
      
      // Create a complete list of images with the cover image first
      const allImages = [];
      
      // Add cover image first if it exists
      if (coverImageUrl && coverImageUrl.trim() !== '') {
        console.log(`Adding cover image for service ${item.name}:`, coverImageUrl);
        allImages.push({
          id: 'cover',
          service_id: serviceId,
          image_url: coverImageUrl,
          created_at: item.created_at
        });
      }
      
      // Add the additional images, avoiding duplicates
      validImages.forEach(img => {
        // Skip if this image is the same as the cover image
        if (img.image_url !== coverImageUrl) {
          allImages.push(img);
        } else {
          console.log(`Skipping duplicate image (same as cover):`, img.image_url);
        }
      });
      
      console.log(`Final image list for service ${item.name}:`, {
        totalImages: allImages.length,
        imageUrls: allImages.map(img => img.image_url)
      });
      
      if (!serviceMap.has(serviceId)) {
        serviceMap.set(serviceId, {
          ...item,
          images: allImages,
          selectedImageIndex: 0
        });
      } else {
        const existing = serviceMap.get(serviceId);
        if (allImages.length > 0) {
          // Replace the images array with our properly ordered version
          existing.images = allImages;
        }
      }
    });
    
    const processedServices = Array.from(serviceMap.values());
    console.log('Processed services:', processedServices.map(s => ({
      id: s.id,
      name: s.name,
      imageCount: s.images.length,
      imageUrls: s.images.map(img => img.image_url),
      firstImageUrl: s.images.length > 0 ? s.images[0].image_url : 'none'
    })));
    
    return processedServices;
  };

  const loadMore = useCallback(async () => {
    console.log("Load more function called but ignored since all services are loaded at once");
    return;
  }, []);

  const handleImageNavigation = useCallback((serviceId: string, direction: 'prev' | 'next') => {
    setServices(currentServices => {
      return currentServices.map(service => {
        if (service.id === serviceId && service.images.length > 1) {
          const currentIndex = service.selectedImageIndex;
          const imagesCount = service.images.length;
          
          let newIndex;
          if (direction === 'next') {
            newIndex = (currentIndex + 1) % imagesCount;
          } else {
            newIndex = (currentIndex - 1 + imagesCount) % imagesCount;
          }
          
          return { ...service, selectedImageIndex: newIndex };
        }
        return service;
      });
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        const profileData = await fetchProfile();
        
        if (!profileData || !isMounted) {
          setIsLoading(false);
          return;
        }
        
        setProfile(profileData);
        
        await recordPageView(profileData.id);
        
        const categoriesData = await fetchVisibleCategories(profileData.id);
        if (isMounted) {
          setCategories(categoriesData);
        }
        
        const { data: servicesData } = await fetchServices(
          profileData.id,
          categoryId,
          searchTerm
        );
        
        if (isMounted) {
          console.log('Services data loaded:', { count: servicesData.length });
          setServices(servicesData);
          setHasMore(false);
          // Small delay before turning off loading to ensure components are ready
          setTimeout(() => {
            if (isMounted) {
              setIsLoading(false);
            }
          }, 100);
          return; // Exit early to prevent setting loading to false immediately
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        if (isMounted) {
          toast({
            variant: "destructive", 
            title: "Error",
            description: "Failed to load service page. Please try again."
          });
          setServicePageNotFound(true);
        }
      } finally {
        if (isMounted) {
          // Only set loading to false here if we haven't returned early after loading services
          setTimeout(() => {
            if (isMounted) {
              setIsLoading(false);
            }
          }, 100);
        }
      }
    };
    
    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, [fetchProfile, recordPageView, fetchServices, categoryId, searchTerm, toast]);

  return {
    profile,
    services,
    categories,
    isLoading,
    isLoadingMore,
    hasMore,
    servicePageNotFound,
    searchTerm,
    categoryId,
    setSearchTerm,
    setCategoryId,
    handleImageNavigation,
    loadMore,
    resetPagination
  };
}
